import {MaterialCommunityIcons as Icon} from "@expo/vector-icons"
import {assert, getPlatform, unreachable} from "@prosay/common"
import {useAsyncEffect} from "@prosay/components"
import {translate} from "@prosay/i18n"
import {getLogger} from "@prosay/logging"
import {Notifications} from "expo"
import React, {useEffect} from "react"
import {AsyncStorage, StyleSheet, View} from "react-native"
import {ActivityIndicator, Appbar} from "react-native-paper"
import {
    Actions,
    Lightbox,
    Router,
    Scene,
    Stack,
    Tabs,
} from "react-native-router-flux"
import {NavigationInjectedProps} from "react-navigation"
import {
    useCurrentUserQuery,
    useSubmitNotificationTokenMutation,
} from "./graphql"
import {
    AcceptConversation,
    Authenticate,
    ClientConversations,
    GettingStarted,
    LawyerConversations,
    Messages,
    Profile,
    SelectTopicAndCategory,
    WriteDescription,
} from "./screen"

const logger = getLogger(__filename)

const styles = StyleSheet.create({
    container: {
        alignItems: "center",
        justifyContent: "center",
        marginVertical: 500,
    },
    scene: {
        flex: 1,
    },
})

const showGettingStartedPage = async (key = "GettingStartedVisited") => {
    const visited = await AsyncStorage.getItem(key)
    if (visited === null) {
        Actions.gettingStarted()
        await AsyncStorage.setItem(key, new Date().toISOString())
    }
}

export interface AuthLoadingNavigationProps {}
export const AuthLoading: React.FC<AuthLoadingNavigationProps> = () => {
    const [mutate] = useSubmitNotificationTokenMutation()
    const {data, error, loading} = useCurrentUserQuery()

    useEffect(() => {
        if (!error) {
            return
        }
        logger.error`Could not find an active user. Going back to authenticate page. See the following error: ${error}.`
        Actions.register()
    }, [error])

    useAsyncEffect(async () => {
        if (loading) {
            return
        }

        if (!data || !data.currentUser) {
            logger.warn` Could not find an active user. Going back to authenticate page.`
            Actions.register()
            return
        }

        const {
            hasNotificationAccess,
            status,
            user: {__typename},
        } = data.currentUser
        assert(__typename === "Client" || __typename === "Lawyer")

        switch (status) {
            case "NotVerified":
                logger.debug`User is not verified and hasNotificationAccess is ${hasNotificationAccess}`
                Actions.register()
                break
            case "Verified":
                logger.debug`User is verified and hasNotificationAccess is ${hasNotificationAccess}`

                // TODO: Make sure this works on web for the future. NOTE: This is supported now in Expo 36. We must wait for official release though.
                if (getPlatform() === "web") {
                    logger.warn`Web notifications are currently not supported. Igonring adding Expo push token`
                } else {
                    await mutate({
                        variables: {
                            token: await Notifications.getExpoPushTokenAsync(),
                        },
                    })
                }

                switch (__typename) {
                    case "Client":
                        Actions.client()
                        showGettingStartedPage()
                        break
                    case "Lawyer":
                        Actions.lawyer()
                        break
                    default:
                        unreachable()
                }

                break
            default:
                unreachable()
        }
    }, [data, loading])

    return (
        <View style={styles.container}>
            <ActivityIndicator />
        </View>
    )
}

export interface NavigationScene {
    descriptor: {
        state: {
            params: {
                title: string
            }
        }
    }
    index: number
}
export interface HeaderProps {
    scene: NavigationScene
}
export const Header: React.FC<HeaderProps & NavigationInjectedProps> = ({
    scene,
}) => (
    <Appbar.Header>
        {scene.index !== 0 && (
            <Appbar.BackAction onPress={() => Actions.pop()} />
        )}
        <Appbar.Content title={scene.descriptor.state.params.title} />
    </Appbar.Header>
)

export interface AppScreenProps {}
export const AppScreen: React.FC<AppScreenProps> = () => (
    <Router sceneStyle={styles.scene}>
        <Lightbox>
            <Scene key="root" hideNavBar>
                <Scene key="authenticating" component={AuthLoading} />
                <Scene key="register" component={Authenticate} />
                <Tabs key="client">
                    <Stack
                        key="createConversation"
                        title={translate(
                            "ui.navigation.tabBar.createConversation",
                        )}
                        icon={() => <Icon name="message-plus" size={25} />}>
                        <Scene
                            key="selectTopicAndCategory"
                            component={SelectTopicAndCategory}
                            title={translate(
                                "ui.navigation.tabBar.createConversation",
                            )}
                            navBar={Header}
                            initial
                            hideNavBar
                        />
                        <Scene
                            key="writeDescription"
                            component={WriteDescription}
                            title={translate(
                                "ui.navigation.tabBar.createConversation",
                            )}
                        />
                    </Stack>
                    <Stack
                        key="conversations"
                        title={translate("ui.navigation.tabBar.conversations")}
                        icon={() => (
                            <Icon name="format-list-bulleted" size={25} />
                        )}
                        initial>
                        <Scene
                            key="conversations"
                            component={ClientConversations}
                            title={translate(
                                "ui.navigation.tabBar.conversations",
                            )}
                            navBar={Header}
                            hideNavBar
                        />
                        <Scene
                            key="messages"
                            component={Messages}
                            title={translate("ui.navigation.tabBar.messages")}
                            hideNavBar
                            hideTabBar
                        />
                    </Stack>
                    <Scene
                        key="profile"
                        component={Profile}
                        title={translate("ui.navigation.tabBar.profile")}
                        navBar={Header}
                        icon={() => <Icon name="account-box" size={25} />}
                    />
                </Tabs>
                <Tabs key="lawyer">
                    <Scene
                        key="accept"
                        component={AcceptConversation}
                        title={translate(
                            "ui.navigation.tabBar.acceptConversation",
                        )}
                        navBar={Header}
                        icon={() => <Icon name="message-plus" size={25} />}
                    />
                    <Stack
                        key="conversations"
                        title={translate("ui.navigation.tabBar.conversations")}
                        icon={() => (
                            <Icon name="format-list-bulleted" size={25} />
                        )}
                        initial
                        hideNavBar>
                        <Scene
                            key="conversations"
                            component={LawyerConversations}
                            title={translate(
                                "ui.navigation.tabBar.conversations",
                            )}
                            navBar={Header}
                        />
                        <Scene
                            key="messages"
                            component={Messages}
                            title={translate("ui.navigation.tabBar.messages")}
                            hideNavBar
                            hideTabBar
                        />
                    </Stack>
                    <Scene
                        key="profile"
                        component={Profile}
                        title={translate("ui.navigation.tabBar.profile")}
                        navBar={Header}
                        icon={() => <Icon name="account-box" size={25} />}
                    />
                </Tabs>
            </Scene>
            <Scene key="gettingStarted" component={GettingStarted} />
        </Lightbox>
    </Router>
)
